/**
 * The small screen an email link opens (docs/specs/cloud/sign-in-security.md §3.1): the link's button ("Sign in to
 * Teslyn", "Accept the invitation", "Confirm the change") and "Not now". Nothing is spent until the button is
 * pressed, so a mail scanner that opened the link first (Microsoft 365's Safe Links, 3 Nov 2026) has spent nothing,
 * and the email's code still works. The press runs `PendingLink.finish` (auth/authReturn.ts: `verifyOtp` with the
 * token hash, under the M3 rules when this browser is signed in already) and hands its words, if any, to the page.
 */
import { useState, type ReactElement } from 'react';
import { Button } from '../components/Button.tsx';
import { PoweredBy } from '../components/States.tsx';
import { LegalLinks } from '../legal/LegalPage.tsx';
import type { Tenant } from '../tenant/tenant.ts';
import { LINK_UNSPENT_WORDS, linkWords, type PendingLink } from './authReturn.ts';

export function LinkConfirm({
  link,
  tenant,
  onDone,
}: {
  readonly link: PendingLink;
  readonly tenant: Pick<Tenant, 'logo' | 'name' | 'productTitle'>;
  /** The link was spent (`words`: why it did not sign the person in, or null) or declined (null). */
  readonly onDone: (words: string | null) => void;
}): ReactElement {
  const w = linkWords(link.type);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function press(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      onDone(await link.finish());
    } catch (e: unknown) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="link-title">
        {tenant.logo ? (
          <img className="signin__logo" src={tenant.logo} alt={tenant.name} />
        ) : (
          <span className="heading">{tenant.name}</span>
        )}
        <h1 id="link-title" className="title">
          {w.title}
        </h1>
        <p className="body">
          {w.body} {LINK_UNSPENT_WORDS}
        </p>
        {error && (
          <p className="field__error caption" role="alert">
            {error}
          </p>
        )}
        <Button variant="primary" busy={busy} onClick={() => void press()}>
          {busy ? 'One moment…' : w.button}
        </Button>
        <Button
          variant="quiet"
          disabled={busy}
          onClick={() => {
            link.dismiss();
            onDone(null);
          }}
        >
          Not now
        </Button>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}
